/**
 * Angular App Module - Hybrid Bootstrap
 *
 * This module sets up Angular to run alongside AngularJS using ngUpgrade.
 * During the migration, both frameworks coexist and can share services/components.
 *
 * Migration Pattern:
 * 1. New features are built in Angular
 * 2. Existing AngularJS services can be upgraded for use in Angular
 * 3. New Angular services can be downgraded for use in AngularJS
 * 4. Components can be upgraded/downgraded as needed
 * 5. Lit web components can be used in both frameworks
 */
import { DoBootstrap, ErrorHandler, NgModule, ApplicationRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { UpgradeModule } from '@angular/upgrade/static';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as Sentry from '@sentry/angular';

// Import Lit web components - registers them as custom elements
import '@acme/ui';

import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { HybridRouterModule } from './routing/hybrid-router.module';
import { registerDowngradedComponents } from './upgrade/ng-downgraded-components';

// Standalone feature components - add migrated views here
import { ExampleComponent } from './features';
import { ZendeskLoginComponent } from './features/zendesk-login/zendesk-login.component';
import { ZendeskLogoutComponent } from './features/zendesk-logout/zendesk-logout.component';

// NOTE: We do NOT import the AngularJS module here to avoid pulling in
// the entire AngularJS codebase into Angular's compilation chain.
// The AngularJS module name will be passed at bootstrap time.

/**
 * Token to provide the AngularJS module name at runtime
 */
export const ANGULARJS_MODULE_NAME = 'ANGULARJS_MODULE_NAME';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    UpgradeModule,
    CoreModule,
    SharedModule,
    HybridRouterModule,
    // Standalone feature components - migrated views
    ExampleComponent,
    ZendeskLoginComponent,
    ZendeskLogoutComponent,
  ],
  providers: [{ provide: ErrorHandler, useValue: Sentry.createErrorHandler() }],
  // Allow Lit web components (custom elements) in Angular templates
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule implements DoBootstrap {
  private static angularJsModuleName: string;
  private static bootstrapped = false;

  static setAngularJsModuleName(name: string) {
    AppModule.angularJsModuleName = name;
  }

  ngDoBootstrap(appRef: ApplicationRef) {
    if (AppModule.bootstrapped) {
      console.warn('[AppModule] ngDoBootstrap called more than once — skipping duplicate bootstrap.');
      return;
    }
    AppModule.bootstrapped = true;

    const injector = appRef.injector;
    const upgrade = injector.get(UpgradeModule);

    if (!AppModule.angularJsModuleName) {
      throw new Error(
        '[AppModule] AngularJS module name not set. Call AppModule.setAngularJsModuleName() before bootstrap.'
      );
    }

    registerDowngradedComponents();

    upgrade.bootstrap(document.documentElement, [AppModule.angularJsModuleName], {
      strictDi: false,
    });
  }
}

